import type { MentionActorSource } from '../../state/mentionUtils';
import { resolveDialogueSpeakerChip, type DialogueLine } from '../dialogueLines';
import { DialogueSpeakerChip } from './DialogueSpeakerChip';
import { dialogueSpeakerGroupKey, groupConsecutiveLines } from './speakerLineGroups';
import styles from './DialogueLines.module.css';

// actors 省略時の既定値 (mentionUtils 利用箇所と同じ理由: 毎回新しい配列リテラルを
// 渡すと呼び出し側の useMemo 依存が変わり続けるため、安定した参照を1つ共有する)。
const EMPTY_ACTORS: MentionActorSource[] = [];

/**
 * 構造化モード (`dialogue_lines` が非 NULL) のセリフ読み取り表示。
 * 話者は「人物名」ではなく、編集モードのトリガーと同じデザイン (size='lg':
 * 枠・塗りなしの円形顔写真+右下隅の A/B/C バッジオーバーレイ) で表示する
 * (2026-08-17 ユーザー指示で 'sm' チップから統一)。actors (mentionActorSources) に
 * 見つからない話者 (自由ラベル・stale な actor_id) は従来どおり label テキストへ
 * フォールバックする。docs/features/scenario-dialogue-speaker-plan.md §3.8。
 *
 * 2026-09-18: テロップ欄と見分けやすくするため、文言は話者チップから伸びる吹き出し
 * (薄い塗り・枠なし) に入れて「喋っている」見た目にする。同一話者が連続する行は
 * 1 つの吹き出しにまとめ、顔写真も 1 枚だけ出す (speakerLineGroups.ts)。
 */
export function DialogueLinesView({ lines, actors = EMPTY_ACTORS }: { lines: DialogueLine[]; actors?: MentionActorSource[] }) {
  if (lines.length === 0) {
    return <span className={styles.emptyHint}>（セリフなし）</span>;
  }
  const groups = groupConsecutiveLines(lines, (line) => dialogueSpeakerGroupKey(line.speaker));
  return (
    <div className={styles.view} data-testid="dialogue-lines-view">
      {groups.map((group) => {
        const speaker = group.lines[0]!.speaker;
        const chip = resolveDialogueSpeakerChip(speaker, actors);
        return (
          <div className={styles.viewRow} key={group.firstIndex} data-speaker-key={group.key}>
            {chip ? (
              <DialogueSpeakerChip badge={chip.badge} thumbnailUrl={chip.thumbnailUrl} name={chip.name} size="lg" />
            ) : speaker.kind === 'narration' ? (
              // 声の台本 (motion-v2) のナレーター行: マイクの円形チップ
              <DialogueSpeakerChip badge="" thumbnailUrl={null} name={speaker.label || 'ナレーター'} size="lg" glyph="mic" />
            ) : (
              // 自由入力・stale な actor_id の話者は同じ円サイズのダミー人物アイコン
              // (バッジなし・頭文字を重ねる・ホバーで label を表示)。
              <DialogueSpeakerChip badge="" thumbnailUrl={null} name={speaker.label} size="lg" />
            )}
            <div className={styles.bubble}>
              {group.lines.map((line, idx) => (
                <span className={styles.lineText} key={idx}>{line.text}</span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
